import { useCallback, useRef, useState, type Dispatch, type SetStateAction } from 'react'
import { useDraggable } from '@dnd-kit/core'
import type { DragStartEvent, DragEndEvent, DragOverEvent } from '@dnd-kit/core'
import { useDataSource } from '../../../lib/dataSource'
import type { Task, Section } from '../../../lib/db/models'
import type { OptimisticUpdates } from './useTaskDragAndDrop'
import { planSectionReorder } from '../../../lib/order/sectionReorder'

export type OptimisticSectionUpdates = Map<string, { order: number }> | null

interface RenderSegment {
  section: Section | null
  tasks: Task[]
  taskCount: number
}

interface UseSectionDragParams {
  segments: RenderSegment[]
  tasks: Task[] | undefined
  sections: Section[] | undefined
  wrapperRef: React.RefObject<HTMLDivElement | null>
  setOptimisticUpdates: Dispatch<SetStateAction<OptimisticUpdates>>
  setOptimisticSectionUpdates: Dispatch<SetStateAction<OptimisticSectionUpdates>>
}

/** Prefix for section draggable IDs — used to discriminate from task drags. */
export const SECTION_ID_PREFIX = 'section::'
/** Prefix for section drop zone IDs. */
export const SECTION_GAP_PREFIX = 'section-gap::'
/** Prefix for empty-section task drop zones — allows dropping tasks into sections with 0 tasks. */
export const EMPTY_SECTION_PREFIX = 'empty-section::'

/** FLIP drop animation data — captured before state clears, consumed by useSectionDragAnimation. */
export interface SectionDropAnim {
  sectionId: string
  x: number
  y: number
}

export function useSectionDrag({ segments, tasks, sections, wrapperRef, setOptimisticUpdates, setOptimisticSectionUpdates }: UseSectionDragParams) {
  const source = useDataSource()
  // State drives re-renders (overlay, collapse classes, drop zone active state).
  const [activeSectionDragId, setActiveSectionDragId] = useState<string | null>(null)
  // Which drop zone the pointer is closest to (drives swap animation).
  const [hoverTargetIdx, setHoverTargetIdx] = useState<number | null>(null)
  // Ref is always current — immune to React batching / stale closure issues.
  const dragIdRef = useRef<string | null>(null)

  // Snapshot of segment DOMRects at drag start — used for counter-transforms & swap calc.
  const segmentRectsRef = useRef<Map<number, DOMRect>>(new Map())
  // Height of just the section header (not the full segment with tasks) — used for swap shift amounts.
  const sourceHeaderHeightRef = useRef<number>(0)
  // Y position of the source header BEFORE CSS changes — used to compute dynamic overlay offset.
  const sourceHeaderTopRef = useRef<number>(0)
  // FLIP drop animation: overlay position captured before state clears.
  const dropAnimRef = useRef<SectionDropAnim | null>(null)

  // ── Drag lifecycle handlers (called from the shared DndContext) ──

  /** Returns true if this event is a section drag (handled here), false if task drag. */
  const handleDragStart = useCallback((event: DragStartEvent): boolean => {
    const id = String(event.active.id)
    if (!id.startsWith(SECTION_ID_PREFIX)) return false
    const sectionId = id.slice(SECTION_ID_PREFIX.length)
    dragIdRef.current = sectionId

    // Snapshot all segment rects BEFORE React commits the CSS changes
    // (drop zones expand, margins collapse, source collapses).
    const wrapper = wrapperRef.current
    if (wrapper) {
      const segEls = wrapper.querySelectorAll<HTMLElement>('[data-segment-idx]')
      segmentRectsRef.current.clear()
      segEls.forEach((el) => {
        const idx = parseInt(el.getAttribute('data-segment-idx')!, 10)
        segmentRectsRef.current.set(idx, el.getBoundingClientRect())
      })
      // Snapshot the source's section header height (just the header, not tasks).
      const headerEl = wrapper.querySelector<HTMLElement>(`.section-header[data-section-id="${sectionId}"]`)
      const headerRect = headerEl?.getBoundingClientRect()
      sourceHeaderHeightRef.current = headerRect?.height ?? 0
      sourceHeaderTopRef.current = headerRect?.top ?? 0
    }

    setActiveSectionDragId(sectionId)
    return true
  }, [wrapperRef])

  const handleDragOver = useCallback((event: DragOverEvent) => {
    const overId = event.over ? String(event.over.id) : null
    if (!overId?.startsWith(SECTION_GAP_PREFIX)) {
      setHoverTargetIdx(null)
      return
    }
    const idx = parseInt(overId.slice(SECTION_GAP_PREFIX.length), 10)
    setHoverTargetIdx(idx)
  }, [])

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const dragId = dragIdRef.current
    if (!dragId) return

    // Capture overlay position for FLIP settle BEFORE clearing state
    const overlayEl = document.querySelector('.section-drag-overlay')
    if (overlayEl) {
      const rect = overlayEl.getBoundingClientRect()
      dropAnimRef.current = { sectionId: dragId, x: rect.left, y: rect.top }
    }

    // Clear ref + state
    dragIdRef.current = null
    setHoverTargetIdx(null)

    // Read drop target directly from the dnd-kit event
    const overId = event.over ? String(event.over.id) : null
    if (!overId?.startsWith(SECTION_GAP_PREFIX)) {
      setActiveSectionDragId(null)
      return
    }
    const targetIdx = parseInt(overId.slice(SECTION_GAP_PREFIX.length), 10)

    if (!tasks || !sections) {
      setActiveSectionDragId(null)
      return
    }

    // Find the source segment index
    const sourceSegIdx = segments.findIndex(seg => seg.section?.id === dragId)
    if (sourceSegIdx === -1) {
      setActiveSectionDragId(null)
      return
    }

    // Skip if dropping at the same position (before or after self)
    if (targetIdx === sourceSegIdx || targetIdx === sourceSegIdx + 1) {
      setActiveSectionDragId(null)
      return
    }

    const sourceSeg = segments[sourceSegIdx]

    // Build the new segment order (sections only, excluding unsectioned)
    const sectionSegments = segments.filter(s => s.section !== null)
    const sectionSourceIdx = sectionSegments.findIndex(s => s.section!.id === dragId)

    // Compute effective target in the section-only list
    let effectiveTarget = 0
    for (let i = 0; i < segments.length && i < targetIdx; i++) {
      if (segments[i].section !== null) effectiveTarget++
    }
    // Adjust for removal of the source
    if (sectionSourceIdx < effectiveTarget) effectiveTarget--

    // Build new section order
    const remaining = sectionSegments.filter(s => s.section!.id !== dragId)
    remaining.splice(effectiveTarget, 0, sourceSeg)

    // Die Rechnung kennt ALLE Aufgaben (auch eingeklappte): segments[].tasks hat nur die sichtbaren.
    const updates = planSectionReorder(tasks, sections, remaining.map(seg => seg.section!.id))

    if (updates.length === 0) {
      setActiveSectionDragId(null)
      return
    }

    // ── Optimistic updates: apply synchronously so the UI reorders immediately ──
    const taskOptimistic = new Map<string, { order: number; sectionId?: string | null }>()
    const sectionOptimistic = new Map<string, { order: number }>()
    for (const u of updates) {
      if (u.table === 'task') {
        taskOptimistic.set(u.id, { order: u.order })
      } else {
        sectionOptimistic.set(u.id, { order: u.order })
      }
    }
    if (taskOptimistic.size > 0) setOptimisticUpdates(taskOptimistic)
    if (sectionOptimistic.size > 0) setOptimisticSectionUpdates(sectionOptimistic)

    // Clear drag state AFTER setting optimistic updates (same React batch)
    setActiveSectionDragId(null)

    // ── Async DB writes (fire-and-forget) ──
    // Batch task writes into a single reorderTasks call so the live query
    // fires only once (avoids flickering from partial updates).
    const writeDb = async () => {
      const sectionWrites = updates.filter(u => u.table === 'section')
      const taskWrites = updates.filter(u => u.table === 'task')
      for (const u of sectionWrites) {
        await source.updateSection(u.id, { order: u.order })
      }
      if (taskWrites.length > 0) {
        await source.reorderTasks(taskWrites.map(u => ({ id: u.id, order: u.order })))
      }
    }
    writeDb()
  }, [segments, tasks, sections, setOptimisticUpdates, setOptimisticSectionUpdates, source])

  const handleDragCancel = useCallback(() => {
    dragIdRef.current = null
    segmentRectsRef.current.clear()
    setHoverTargetIdx(null)
    setActiveSectionDragId(null)
  }, [])

  return {
    activeSectionDragId,
    hoverTargetIdx,
    dropAnimRef,
    segmentRectsRef,
    sourceHeaderHeightRef,
    sourceHeaderTopRef,
    handleSectionDragStart: handleDragStart,
    handleSectionDragOver: handleDragOver,
    handleSectionDragEnd: handleDragEnd,
    handleSectionDragCancel: handleDragCancel,
  }
}

/**
 * Hook for individual SectionHeader to register as draggable.
 * Call once per section in the segment map.
 */
export function useSectionDraggable(sectionId: string) {
  return useDraggable({ id: `${SECTION_ID_PREFIX}${sectionId}` })
}
